import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Form, Container, Button, Toast, Spinner } from "react-bootstrap";
import { api } from "../../../CONSTANTS";
import styles from "./contactForm.module.css";
import { axiosPost } from "../../../UTILS/AXIOS";
import FormSecurity from "../../FORM_SECURITY";
import {
  rules,
  securityFields,
  isSuccess,
  failureMessage,
  applyServerErrors,
} from "../../../UTILS/VALIDATION";

const options = [
  { value: "Sponsor", label: "Sponsor" },
  { value: "Physician", label: "Physician" },
  { value: "Patient", label: "Patient" },
  { value: "Other", label: "Other" },
];

const schema = Yup.object().shape({
  fullName: rules.name("Full Name"),
  email: rules.email(),
  phone: rules.phone(),
  address: rules.text("Residential Address", 200),
  city: rules.text("City", 100),
  state: rules.text("State", 100),
  contactType: Yup.string()
    .oneOf(options.map((o) => o.value), "Select an option")
    .required("Select an option"),
  message: rules.text("Message/Query", 2000),
  ...securityFields,
});

const formFields = [
  { name: "fullName", label: "Full Name", type: "text", required: true, autoComplete: "name" },
  { name: "email", label: "Email Address", type: "email", required: true, autoComplete: "email" },
  { name: "phone", label: "Phone Number", type: "tel", required: true, autoComplete: "tel" },
  { name: "address", label: "Residential Address", type: "text", required: true, autoComplete: "street-address" },
  { name: "city", label: "City", type: "text", required: true, autoComplete: "address-level2" },
  { name: "state", label: "State", type: "text", required: true, autoComplete: "address-level1" },
  { name: "contactType", label: "I am a prospective", type: "dropdown", options, required: true },
  { name: "message", label: "Message / Query", type: "textarea", required: true },
];

const ContactForm = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [loading, setLoading] = useState(false);
  const captcha = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), mode: "onTouched" });

  const onSubmit = async (data) => {
    setLoading(true);
    const result = await axiosPost(api, { ...data, formType: "generalContact" });
    if (isSuccess(result)) {
      setToastMessage("Thank you. We'll be in touch soon.");
      setToastVariant("success");
      reset();
    } else {
      applyServerErrors(result, setError, Object.keys(schema.fields));
      setToastMessage(failureMessage(result));
      setToastVariant("danger");
    }
    captcha.current?.reset();
    setShowToast(true);
    setLoading(false);
  };

  const renderField = ({ name, label, type, options: opts, required, autoComplete }) => (
    <Form.Group controlId={`form${name}`} key={name}>
      <Form.Label className={styles.label}>
        {label}
        {required && <span className={styles.contactFormTextDanger}> *</span>}
      </Form.Label>

      {type === "textarea" && (
        <Form.Control
          as="textarea"
          rows={3}
          maxLength={2000}
          {...register(name)}
          aria-required={required}
          isInvalid={!!errors[name]}
          disabled={loading}
          className={styles.contactFormFormControl}
        />
      )}
      {type === "dropdown" && (
        <Form.Select
          {...register(name)}
          aria-required={required}
          isInvalid={!!errors[name]}
          disabled={loading}
          className={styles.contactFormFormSelect}
        >
          <option value="">Select an option</option>
          {opts.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )}
      {["text", "tel", "email"].includes(type) && (
        <Form.Control
          type={type}
          {...register(name)}
          autoComplete={autoComplete}
          aria-required={required}
          isInvalid={!!errors[name]}
          disabled={loading}
          className={styles.contactFormFormControl}
        />
      )}

      <Form.Control.Feedback type="invalid">{errors[name]?.message}</Form.Control.Feedback>
    </Form.Group>
  );

  return (
    <Container className={`${styles.contactForm} position-relative`}>
      {loading && (
        <div className={styles.overlay}>
          <Spinner animation="border" variant="light" />
        </div>
      )}

      <Form noValidate onSubmit={handleSubmit(onSubmit)} className={styles.contactFormFormSection}>
        {formFields.map(renderField)}

        <FormSecurity ref={captcha} register={register} setValue={setValue} error={errors.recaptchaToken} theme="dark" />

        <Form.Group>
          <div className="d-flex justify-content-center mt-4">
            <Button type="submit" disabled={loading} className={styles.contactFormFormSubmit}>
              {loading ? <Spinner animation="border" size="sm" /> : "Submit"}
            </Button>
          </div>
        </Form.Group>
      </Form>

      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={6000}
        autohide
        role="status"
        className={`${styles.toastCustom} bg-${toastVariant} text-white position-fixed`}
        style={{ bottom: "10px", right: "10px", zIndex: 10000 }}
      >
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
    </Container>
  );
};

export default ContactForm;
